import React, { FC, useCallback } from "react";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { X } from "feather-icons-react";
import { useTranslation } from "react-i18next";

import { GradeRange } from "../../../../hooks";

import { Button } from "@components/base";
import { GradeEstimatorResponseDto } from "@api/hooks/paygap";

/**
 * Get the grade description based on current language.
 * Falls back to: descriptionTranslated[lang] -> descriptionTranslated.en -> description
 */
const getGradeDescription = (
  grade: GradeEstimatorResponseDto,
  lang: string,
): string => {
  const translations = grade.descriptionTranslated as Record<string, string> | undefined;

  if (translations) {
    if (translations[lang]) return translations[lang];
    if (translations.en) return translations.en;
  }

return grade.description;
};

interface GradeResultsDisplayProps {
  displayGrades: GradeEstimatorResponseDto[] | null;
  gradeRanges: GradeRange[];
  isModelLoading: boolean;
  modelError: string | null;
  showSaveSuccess: boolean;
  canRunEstimation: boolean;
  onRunGradeEstimation: () => void;
  onClose: () => void;
  onSaveGrades: () => void;
  highlightRunButton?: boolean;
  hideBottomButtons?: boolean;
}

export const GradeResultsDisplay: FC<GradeResultsDisplayProps> = ({
  displayGrades,
  gradeRanges,
  isModelLoading,
  modelError,
  showSaveSuccess,
  canRunEstimation,
  onRunGradeEstimation,
  onClose,
  onSaveGrades,
  highlightRunButton = false,
  hideBottomButtons = false,
}) => {
  const { t, i18n } = useTranslation();

  const getDescription = useCallback(
    (grade: GradeEstimatorResponseDto) => getGradeDescription(grade, i18n.language),
    [i18n.language],
  );

  return (
    <>
      {modelError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {modelError}
        </Alert>
      )}
      <Box
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
          maxHeight: 200,
          overflowY: "auto",
        }}
      >
        <Box
          display="grid"
          gridTemplateColumns="0.5fr 1.3fr 1fr"
          bgcolor="grey.100"
          px={1}
          py={0.5}
          fontWeight={600}
          fontSize="0.85rem"
        >
          <span>{t("paygap.gradeEstimator.id", "ID")}</span>
          <span>{t("paygap.gradeEstimator.descriptionHeader")}</span>
          <span>{t("paygap.gradeEstimator.rangeHeader")}</span>
        </Box>
        {(displayGrades || []).map((row, idx) => {
          const range = gradeRanges[idx] ||
            gradeRanges[gradeRanges.length - 1] || { start: 0, end: 0 };

          return (
            <Box
              key={row.id}
              display="grid"
              gridTemplateColumns="0.5fr 1.3fr 1fr"
              px={1}
              py={0.6}
              sx={{
                borderTop: "1px solid",
                borderColor: "grey.100",
                backgroundColor: idx % 2 === 0 ? "white" : "grey.50",
                fontSize: "0.9rem",
              }}
            >
              <span>{row.id}</span>
              <span>{getDescription(row)}</span>
              <span>
                {range.start} - {range.end}
              </span>
            </Box>
          );
        })}
        {!displayGrades && (
          <Box
            display="grid"
            gridTemplateColumns="0.5fr 1.3fr 1fr"
            px={1}
            py={0.6}
            sx={{
              borderTop: "1px solid",
              borderColor: "grey.100",
              backgroundColor: "white",
              fontSize: "0.9rem",
            }}
          >
            <span>-</span>
            <span>{t("paygap.gradeEstimator.modelPlaceholder")}</span>
          </Box>
        )}
      </Box>

      {!canRunEstimation && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t("paygap.gradeEstimator.requiresPoints")}
        </Alert>
      )}
      <Stack direction="row" justifyContent="flex-end" spacing={1} mt={1}>
        <Button
          variant={highlightRunButton ? "contained" : "outlined"}
          color={highlightRunButton ? "warning" : "primary"}
          isLoading={isModelLoading}
          disabled={isModelLoading || !canRunEstimation}
          onClick={onRunGradeEstimation}
          sx={highlightRunButton ? {
            animation: "pulse 1.5s ease-in-out infinite",
            "@keyframes pulse": {
              "0%": { boxShadow: "0 0 0 0 rgba(237, 108, 2, 0.4)" },
              "70%": { boxShadow: "0 0 0 10px rgba(237, 108, 2, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(237, 108, 2, 0)" },
            },
          } : undefined}
        >
          {t("paygap.gradeEstimator.modelButton")}
        </Button>
      </Stack>
      {isModelLoading && (
        <Typography variant="caption" color="text.secondary">
          {t("paygap.gradeEstimator.loadingModel")}
        </Typography>
      )}

      {showSaveSuccess && (
        <Alert severity="success" sx={{ mb: 1 }}>
          {t("organizationSettings.settingsSaved")}
        </Alert>
      )}

      {!hideBottomButtons && (
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button variant="outlined" onClick={onClose} startIcon={<X size={16} />}>
            {t("paygap.gradeEstimator.close")}
          </Button>
          <Button variant="contained" onClick={onSaveGrades}>
            {t("paygap.gradeEstimator.save")}
          </Button>
        </Stack>
      )}
    </>
  );
};
